import React, { useCallback, useMemo, useState } from "react";

import { Button } from "@app/scripts/react-general-components/Button";
import { useShopify, useShopifyProduct } from "@app/scripts/react-hooks/shopify";

import type { NormalisedProduct, NormalisedVariant, Product } from "@app/scripts/types";

type Props = {
  product?: NormalisedProduct | Product;
  className?: string;
};

export const QuickAddPlp: React.FC<Props> = ({ product, className }) => {
  const { normaliseProduct } = useShopify();
  const normalisedProduct = useMemo(
    () =>
      (product as NormalisedProduct)?.is_normalised
        ? (product as NormalisedProduct)
        : normaliseProduct(product as Product),
    [product, normaliseProduct]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const { error, handleAddToCart, handleChangeVariant, selectedVariantId, setSelectedVariantId, url } =
    useShopifyProduct({
      normalisedProduct,
    });

  const addToCart = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      const success = await handleAddToCart(e);
      if (success) {
        setIsSuccess(true);
      }
    },
    [handleAddToCart]
  );

  const singleVariantAdd = useCallback(
    async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();
      setSelectedVariantId(normalisedProduct.variants?.[0].id);
      if (e.target.classList.contains("disabled")) {
        return (window.location.href = `/products/${product?.handle}`);
      }
      const success = await handleAddToCart(e, normalisedProduct.variants?.[0].id);
      if (success) setIsSuccess(true);
    },
    [isOpen, setIsOpen, handleAddToCart]
  );

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChangeVariant(e);
      if (e.target.classList.contains("disabled")) {
        return (window.location.href = `/products/${product?.handle}?variant=${e.target.value}`);
      }
      const success = await handleAddToCart(e, e.target.value);
      if (success) setIsSuccess(true);
    },
    [selectedVariantId, error, handleChangeVariant]
  );

  const gridColumn =
    product.variants.length >= 7
      ? "product-card__plp-quick-add-variants grid-cols-7"
      : "product-card__plp-quick-add-variants grid-cols-" + product.variants.length;

  return !isSuccess ? (
    <form
      method="post"
      action="/cart/add"
      onSubmit={addToCart}
      className={`product-card__plp-quick-add ${className || ""}`}
    >
      <input name="__id" value={selectedVariantId} type="hidden" />
      {error && <small className="product-card__plp-quick-add-error bg-brand-error text-brand-white">{error}</small>}
      {product?.variants?.length === 1 ? (
        <Button
          label="Quick Add"
          ariaLabel="Quick Add"
          color="white"
          variant="secondary"
          className="swiper-no-swiping product-card__plp-quick-add-button"
          onClick={singleVariantAdd}
        />
      ) : (
        <div className={gridColumn}>
          {product?.variants &&
            product.variants.length > 1 &&
            (product.variants as NormalisedVariant[])?.map((variant, index) => {
              return (
                <div className="product-card__plp-quick-add-item">
                  {variant.currentlyNotInStock === true && (
                    <small
                      data-pos={index % 7}
                      className="product-card__plp-quick-add-oos bg-brand-white text-brand-secondary-grey"
                    >
                      Out Of Stock - Notify Me
                    </small>
                  )}
                  <label data-disabled={!variant.available || variant.currentlyNotInStock === true}>
                    <input
                      type="radio"
                      name="id"
                      className={`product-card__plp-quick-add-variants-button${
                        !variant.available || variant.currentlyNotInStock === true ? " disabled" : ""
                      }`}
                      onChange={handleChange}
                      value={variant.id}
                    />
                    {variant.title.replace(".00", "")}
                  </label>
                </div>
              );
            })}
        </div>
      )}
      <button type="submit" className="hidden" />
    </form>
  ) : null;
};
